import { useEffect, useRef, useState } from "react";
import type { DrawingGuesser, MaliunokView } from "../../shared/maliunok";
import { api } from "../api";
import { DrawCanvas } from "../components/DrawCanvas";
import { GiveUp } from "../components/GiveUp";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";
import { LegacyMaliunok } from "./LegacyMaliunok";

const COPY = {
  draw_first: "Спершу опублікуй свій малюнок 🎨",
  no_drawing_yet: "Цього малюнка ще немає. Онови галерею.",
  bad_word: "Одне українське слово",
  already_guessed: "Це слово вже було",
  not_artist: "Ти сьогодні тільки вгадуєш",
  bad_drawing: "Не вдалось прийняти малюнок. Спробуй ще раз або трохи спрости його.",
  round_finished: "Цей малюнок уже розгадано. Обери інший.",
  own_drawing: "Це твій малюнок 🙂",
};
const button =
  "min-h-11 rounded-xl bg-[rgba(128,128,128,0.13)] px-4 text-sm font-semibold disabled:opacity-40";

export function Maliunok({ date }: { date?: string | undefined }) {
  const g = useGame<MaliunokView>("maliunok", COPY, date);
  const [word, setWord] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const submitting = useRef(false);
  const state = g.screen.kind === "game" ? g.screen.state : null;
  const view = state?.view;
  const unlocked = view?.mode === "gallery" && view.unlocked;
  const draftKey =
    state && view?.mode === "gallery"
      ? `maliunok-draft:${view.playerId}:${state.date}:${view.own?.word ?? ""}`
      : "";

  useEffect(() => {
    if (view?.own?.png && draftKey) {
      try {
        localStorage.removeItem(draftKey);
      } catch {
        /* Private browsing can deny storage. */
      }
    }
  }, [view?.own?.png, draftKey]);

  // Refresh only the unlocked gallery. Never replace a drawing editor or race a guess.
  useEffect(() => {
    if (!unlocked || g.busy) return;
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (document.hidden || pending || submitting.current) return;
      pending = true;
      try {
        const next = await api.state<MaliunokView>("maliunok", date);
        if (active && !submitting.current) g.apply(next);
      } catch {
        /* Keep the gallery usable offline; manual refresh reports errors. */
      } finally {
        pending = false;
      }
    };
    const interval = window.setInterval(() => void refresh(), 15_000);
    const visible = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [unlocked, g.busy, date, g.apply]);

  const run = async (input: string) => {
    if (submitting.current) return;
    submitting.current = true;
    const next = await g.run(() => api.guess<MaliunokView>("maliunok", input, date));
    submitting.current = false;
    if (!next) return;
    const extra = next.extra as { right?: boolean } | undefined;
    if (extra?.right === false) {
      haptic.error();
      g.showToast("Не воно. Спробуй ще 🙂");
    }
    if (extra?.right) g.showToast("Є! Вгадано 🎉");
    setWord("");
  };

  if (g.screen.kind === "loading")
    return (
      <Shell>
        <Centered>Дістаємо олівці…</Centered>
      </Shell>
    );
  if (g.screen.kind === "error")
    return (
      <Shell>
        <Centered>
          <p>{g.screen.message}</p>
          {g.screen.retry && (
            <button className={button} onClick={() => void g.load()}>
              Спробувати ще
            </button>
          )}
        </Centered>
      </Shell>
    );
  const s = g.screen.state;
  const v = s.view;
  if (v.mode !== "gallery") return <LegacyMaliunok date={date} />;
  const active =
    v.drawings.find((d) => d.id === selected) ??
    v.drawings.find((d) => d.status === "playing") ??
    v.drawings[0];
  const solved = v.drawings.filter((d) => d.status === "guessed").length;

  return (
    <Shell
      header={
        <Header
          left={date ? `📼 ${date}` : `#${s.number}`}
          title="МАЛЮНОК"
          right={date ? "" : `🔥 ${s.me.streak}`}
        />
      }
    >
      <main className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {!v.unlocked && v.own ? (
          <>
            <DrawCanvas
              key={draftKey}
              draftKey={draftKey}
              unlockGallery
              word={v.own.word}
              disabled={false}
              busy={g.busy}
              onSave={(png) => {
                void g.run(() => api.drawing<MaliunokView>(png, date));
              }}
            />
            <p className="mb-3 text-center text-xs text-tghint">
              🔒 Малюнки друзів відкриються після публікації твого.
            </p>
          </>
        ) : (
          <div className="mx-auto max-w-[480px]">
            <div className="flex items-center justify-between gap-2 py-3">
              <div>
                <h1 className="text-xl font-extrabold">Малюнки друзів</h1>
                <p className="mt-1 text-xs text-tghint">
                  {v.canDraw ? "Твій малюнок уже в галереї ✓" : "Ти тільки вгадуєш — малювати не треба"}
                </p>
              </div>
              <button
                type="button"
                className={button}
                aria-label="Оновити галерею"
                disabled={g.busy}
                onClick={() => void g.run(() => api.state<MaliunokView>("maliunok", date))}
              >
                ↻
              </button>
            </div>
            {v.own?.png && (
              <section
                className="mb-4 flex gap-3 rounded-2xl bg-[rgba(128,128,128,0.1)] p-3"
                aria-label="Хто вгадав твій малюнок"
              >
                <details className="shrink-0">
                  <summary className="cursor-pointer list-none">
                    <img
                      src={v.own.png}
                      alt="Твій малюнок — натисни, щоб збільшити"
                      className="h-16 w-16 rounded-lg bg-white"
                    />
                  </summary>
                  <img
                    src={v.own.png}
                    alt={`Твій малюнок: ${v.own.word}`}
                    className="mt-2 w-48 rounded-lg bg-white"
                  />
                </details>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold">Твій малюнок · {v.own.word}</h2>
                  <Guessers people={v.own.guessedBy ?? []} playerId={v.playerId} />
                </div>
              </section>
            )}
            {v.drawings.length > 0 ? (
              <>
                <div className="mb-3 flex gap-2 overflow-x-auto pb-2" aria-label="Обери малюнок">
                  {v.drawings.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      aria-pressed={active?.id === d.id}
                      onClick={() => {
                        setSelected(d.id);
                        setWord("");
                      }}
                      className={`w-20 shrink-0 rounded-xl p-1.5 text-left ${active?.id === d.id ? "bg-tgbutton/20 ring-2 ring-inset ring-tgbutton" : "bg-[rgba(128,128,128,0.1)]"}`}
                    >
                      <img
                        src={d.png}
                        alt={`Малюнок: ${d.name}`}
                        className="h-16 w-full rounded-md bg-white object-contain"
                      />
                      <span className="mt-1 block truncate text-xs">
                        {d.status === "guessed" ? "✓ " : d.status === "revealed" ? "◉ " : ""}
                        {d.name}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-tghint">
                        Вгадали: {d.guessedBy?.length ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
                {active && (
                  <section key={active.id} aria-label={`Вгадати малюнок: ${active.name}`}>
                    <div className="mb-2 flex items-baseline justify-between gap-2">
                      <h2 className="font-bold">{active.name}</h2>
                      <span className="text-xs text-tghint">
                        {active.length} літер{active.letter ? ` · починається на «${active.letter}»` : ""}
                      </span>
                    </div>
                    <img
                      src={active.png}
                      alt={`Що зображено на малюнку ${active.name}?`}
                      className="block w-full rounded-lg border border-[rgba(128,128,128,0.25)] bg-white"
                    />
                    <Guessers people={active.guessedBy ?? []} playerId={v.playerId} />
                    {active.status === "playing" ? (
                      <>
                        <form
                          className="mt-3 flex gap-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const input = word.trim();
                            if (input && !g.busy) {
                              setSelected(active.id);
                              void run(`guess:${active.id}:${input}`);
                            }
                          }}
                        >
                          <input
                            aria-label="Твоя відповідь"
                            value={word}
                            onChange={(e) => setWord(e.target.value)}
                            placeholder="Що тут намальовано?"
                            maxLength={20}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            enterKeyHint="send"
                            className="h-12 min-w-0 flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-3 text-base focus:border-tgbutton focus:outline-none"
                          />
                          <button
                            type="submit"
                            disabled={g.busy || !word.trim()}
                            className="rounded-xl bg-tgbutton px-4 font-bold text-tgbuttontext disabled:opacity-35"
                          >
                            {g.busy ? "…" : "Вгадати"}
                          </button>
                        </form>
                        <p className="mt-2 text-xs text-tghint">
                          Спроб скільки хочеш. Після 5 — перша літера.
                        </p>
                      </>
                    ) : (
                      <div className="mt-3 rounded-xl bg-[rgba(128,128,128,0.13)] px-4 py-3" role="status">
                        <span className="text-sm">
                          {active.status === "guessed" ? "✓ Вгадано!" : "Відповідь:"}
                        </span>{" "}
                        <b className="text-lg">{active.word}</b>
                        <p className="mt-1 text-xs text-tghint">
                          {active.status === "guessed"
                            ? `Спроб: ${active.attempts.length}.`
                            : "Наступний точно вгадаєш."}{" "}
                          Обери інший малюнок угорі.
                        </p>
                      </div>
                    )}
                    {active.attempts.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Попередні відповіді">
                        {active.attempts.map((a, i) => (
                          <span
                            key={a}
                            className={`rounded-full bg-[rgba(128,128,128,0.12)] px-3 py-1 text-xs ${active.status === "guessed" && i === active.attempts.length - 1 ? "font-bold" : "text-tghint"}`}
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                    {active.status === "playing" && active.attempts.length >= 3 && (
                      <div className="mt-3 flex">
                        <GiveUp
                          busy={g.busy}
                          onGiveUp={() => {
                            setSelected(active.id);
                            void run(`reveal:${active.id}`);
                          }}
                        />
                      </div>
                    )}
                  </section>
                )}
                <p className="my-4 text-center text-xs text-tghint">
                  Вгадано {solved} з {v.drawings.length}. Нові малюнки з’являються автоматично.
                </p>
              </>
            ) : (
              <div className="my-6 rounded-2xl border border-dashed border-[rgba(128,128,128,0.4)] px-6 py-10 text-center">
                <span className="text-4xl" aria-hidden="true">
                  🎨
                </span>
                <h2 className="mt-3 text-lg font-bold">Друзі ще малюють</h2>
                <p className="mt-2 text-sm text-tghint">
                  Можеш зайнятись іншою грою й повернутись. Малюнки з’являться тут, щойно їх опублікують.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
      <Toast message={g.toast} />
    </Shell>
  );
}

function Guessers({ people, playerId }: { people: DrawingGuesser[]; playerId: number }) {
  return (
    <div className="mt-2 text-xs" aria-label="Хто вгадав">
      {people.length ? (
        <>
          <p className="text-tghint">Вгадали · {people.length}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {people.map((p) => (
              <span key={p.id} className="rounded-full bg-[rgba(128,128,128,0.12)] px-2 py-1">
                ✓ {p.id === playerId ? "Ти" : p.name} <span className="text-tghint">· {p.attempts} спр.</span>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="text-tghint">Ще ніхто не вгадав</p>
      )}
    </div>
  );
}
